import { useEffect, useState } from "react";
import { UserCog, Trash2, Plus, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PERMS = ["dashboard", "enquiries", "bookings", "tours", "destinations", "gallery", "testimonials", "blog", "users", "settings"];
const fmtDate = (d) => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }); };
const ageLabel = (days) => days == null ? "—" : days < 1 ? "Today" : days < 30 ? `${days} days` : days < 365 ? `${Math.floor(days / 30)} mo` : `${Math.floor(days / 365)} yr`;
const dinput = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState("user");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", designation: "", permissions: [] });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const delUser = async (uid) => { if (!window.confirm("Remove this account?")) return; await api.delete(`/admin/users/${uid}`); setUsers(users.filter((u) => u.id !== uid)); };
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const saveStaff = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try { await api.post("/admin/staff", form); toast.success("Staff saved"); setOpen(false); setForm({ name: "", email: "", phone: "", designation: "", permissions: [] }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const list = users || [];
  const regularUsers = list.filter((u) => (u.role || "user") === "user");
  const staff = list.filter((u) => ["staff", "admin"].includes(u.role));
  const th = "p-3 font-semibold";

  return (
    <AdminLayout title="Manage Users & Staff">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 rounded-full bg-gray-100 p-1" data-testid="users-tabs">
          {[["user", `Users (${regularUsers.length})`], ["staff", `Staff (${staff.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`users-tab-${k}`} className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === k ? "brand-gradient text-white" : "text-navy hover:bg-white"}`}>{l}</button>
          ))}
        </div>
        {tab === "staff" && <button onClick={() => setOpen(true)} data-testid="add-staff-btn" className="btn-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"><Plus className="h-4 w-4" /> Add Staff</button>}
      </div>

      {users === null ? <div className="h-40 skeleton rounded-2xl" /> : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white card-soft">
          {tab === "user" ? (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr><th className={th}>User Name</th><th className={th}>Mobile No</th><th className={th}>Email</th><th className={th}>DOB</th><th className={th}>City</th><th className={th}>State</th><th className={th}>Last Login</th><th className={th}>Packages</th><th className={th}>Member Since</th><th className={`${th} text-right`}>Delete</th></tr>
              </thead>
              <tbody>
                {regularUsers.length === 0 ? <tr><td colSpan={10} className="p-10 text-center text-gray-400">No users yet.</td></tr> : regularUsers.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-gray-50">
                    <td className="p-3 font-semibold text-navy">{u.name}</td>
                    <td className="p-3 text-gray-600">{u.phone || "—"}</td>
                    <td className="p-3 text-gray-600">{u.email}</td>
                    <td className="p-3 text-gray-600">{fmtDate(u.dob)}</td>
                    <td className="p-3 text-gray-600">{u.city || "—"}</td>
                    <td className="p-3 text-gray-600">{u.state || "—"}</td>
                    <td className="p-3 text-gray-600">{fmtDate(u.last_login)}</td>
                    <td className="p-3 font-bold text-navy">{u.bookings_count ?? 0}</td>
                    <td className="p-3 text-gray-600">{ageLabel(u.account_age_days)}</td>
                    <td className="p-3 text-right"><button onClick={() => delUser(u.id)} data-testid={`del-user-${u.id}`} className="rounded-lg p-2 text-brandred hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr><th className={th}>Staff Name</th><th className={th}>Designation</th><th className={th}>Mobile</th><th className={th}>Email</th><th className={th}>Permissions</th><th className={`${th} text-right`}>Delete</th></tr>
              </thead>
              <tbody>
                {staff.length === 0 ? <tr><td colSpan={6} className="p-10 text-center text-gray-400">No staff yet.</td></tr> : staff.map((u) => (
                  <tr key={u.id} data-testid={`staff-row-${u.id}`} className="border-t border-gray-50">
                    <td className="p-3 font-semibold text-navy">{u.name} {u.role === "admin" && <span className="ml-1 rounded-full bg-brandorange/15 px-2 py-0.5 text-[10px] font-bold text-brandorange">OWNER</span>}</td>
                    <td className="p-3 text-gray-600">{u.designation || (u.role === "admin" ? "Administrator" : "—")}</td>
                    <td className="p-3 text-gray-600">{u.phone || "—"}</td>
                    <td className="p-3 text-gray-600">{u.email}</td>
                    <td className="p-3"><div className="flex flex-wrap gap-1">{u.role === "admin" ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">All</span> : (u.permissions || []).length ? (u.permissions || []).map((p) => <span key={p} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold capitalize text-gray-600">{p}</span>) : <span className="text-gray-400">—</span>}</div></td>
                    <td className="p-3 text-right">{u.email !== user?.email && <button onClick={() => delUser(u.id)} data-testid={`del-staff-${u.id}`} className="rounded-lg p-2 text-brandred hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-form-dialog" aria-describedby={undefined}><DialogHeader><DialogTitle className="font-display text-2xl text-navy">Add Staff</DialogTitle></DialogHeader>
          <form onSubmit={saveStaff} className="space-y-3">
            <div className="grid grid-cols-2 gap-3"><input data-testid="staff-name" className={dinput} placeholder="Staff Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /><input data-testid="staff-designation" className={dinput} placeholder="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3"><input data-testid="staff-email" className={dinput} placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /><input data-testid="staff-phone" className={dinput} placeholder="Mobile" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase text-gray-400"><ShieldCheck className="h-4 w-4 text-brandorange" /> Permissions Granted</p>
              <div className="grid grid-cols-3 gap-2">
                {PERMS.map((p) => (
                  <label key={p} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold capitalize ${form.permissions.includes(p) ? "border-brandorange bg-orange-50 text-brandorange" : "border-gray-200 text-navy"}`}>
                    <input type="checkbox" data-testid={`perm-${p}`} checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="accent-brandorange" /> {p}
                  </label>
                ))}
              </div>
            </div>
            <button data-testid="save-staff-btn" disabled={saving} className="btn-gradient flex w-full items-center justify-center rounded-full py-3 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Staff"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
